import * as React from 'react'
import { PureComponent } from 'react'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import { connect } from 'react-redux'
import { AppState } from '../store'
import moment from '../utilities/Moment'
import { Api, SettingsController } from '../controllers'
import ERoute from '../ERoute'
import AppLayout from '../layouts/AppLayout'
import Onboarding from './Onboarding'
import LedgerItemEditor from '../layouts/LedgerItemEditor'
import { Toaster } from '../components/ui/sonner'
import ConfirmEmail from './ConfirmEmail'
import Integration from './Integration'
import PushNotification from '../PushNotification'
import DeviceController from '../controllers/DevicesController'
import i18n, { setLocale } from '../translations'
import ActionCableConsumer from '../consumers/ActionCableConsumer'
import { AnyAction, Dispatch } from 'redux'
import { pauseTimer, startTimer, stopTimer } from '../store/timer/actions'
import { updateSettings, updateSubscription, updateWorkspace } from '../store/authentication/actions'
import Workspaces from './Workspaces'
import UserWorkspaceSettingHelper from '../helpers/UserWorkspaceSettingHelper'
import { Helmet } from 'react-helmet';
import { Body } from '../components/Body/Body';
import Modals from '../components/Modals/Modals';
import { CurrentUser, PushNotificationData, PushNotificationDataType, Settings, Sounds, TimeEntryTimerAction, UserWorkspaceSettingScope, WorkspaceChannelEvent, WorkspaceChannelEventType } from '../types';
import { migrateLocaleStorageValues } from '../LocalStorage';
import PlaybooksCreate from './PlaybooksCreate';
import ContractsCreate from './ContractsCreate';
import FormsCreate from './FormsCreate';
import CurrentUserHelper from '../helpers/CurrentUserHelper';
import isMobile from 'is-mobile';
import Subscription from './Account/Subscription';
import { WithTranslation, withTranslation } from 'react-i18next';
import { MessagePayload } from 'firebase/messaging';
import { showConfirmModal } from '../store/modals/actions';
import SequencesCreate from './SequencesCreate';
import Sound from '../audio';
import { TooltipProvider } from '@/components/ui/tooltip';

interface IStateToProps {
  currentUser: CurrentUser
  accessToken: string
  navigationActive: boolean
  appVersion: string
}

interface IDispatchToProps {
  updateWorkspace: typeof updateWorkspace
  updateSubscription: typeof updateSubscription
  startTimer: typeof startTimer
  pauseTimer: typeof pauseTimer
  stopTimer: typeof stopTimer
  updateSettings: typeof updateSettings
  showConfirmModal: typeof showConfirmModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation

class App extends PureComponent<IProps> {
  private versionPing: NodeJS.Timeout

  constructor(props: IProps) {
    super(props)

    this.onActionCableConnected = this.onActionCableConnected.bind(this)
    this.onActionCableDisconnected = this.onActionCableDisconnected.bind(this)
    this.onActionCableReceived = this.onActionCableReceived.bind(this)
    this.onPushNotificationMessage = this.onPushNotificationMessage.bind(this)
    this.onServiceWorkerMessage = this.onServiceWorkerMessage.bind(this)
    this.onI18nInitialized = this.onI18nInitialized.bind(this)
    this.onAppVersionPing = this.onAppVersionPing.bind(this)
    this.onVisibilityChange = this.onVisibilityChange.bind(this)
  }

  componentWillMount() {
    const {
      accessToken,
      currentUser: {
        locale,
        workspace,
      }
    } = this.props

    // Migrate old locale storage values if needed
    migrateLocaleStorageValues()

    // Set API token
    Api.setToken(accessToken)

    // Set moment defaults
    moment.locale(locale)

    // Set day of the week in momentjs
    if (workspace?.setting?.start_week_on === 'monday') {
      moment.updateLocale(locale, { week: { dow: 1 } });
    }

    // Set i18next locale
    setLocale(locale)

    // Set default timezone
    if (workspace) moment.tz.setDefault(workspace.timezone)

    // Preload common UI sounds so first playback does not wait on fetch/decode.
    Sound.preload([Sounds.COMPLETE, Sounds.STEP_COMPLETE, Sounds.NOTIFICATION_ADMIN])

    // Firebase messaging setup
    this.pushNotificationSetup().catch(console.error)

    // Add an interval to check for new app versions (every 5 minutes)
    this.versionPing = setInterval(this.onAppVersionPing, 5 * 60 * 1000)
  }

  componentDidMount() {
    // Service worker message handler: receives CALL_HANDOVER when tapping a call notification
    // while the app was already open in the background (service worker postMessages us).
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', this.onServiceWorkerMessage)
    }

    // Handle pending call when the app was closed and opened via a notification tap.
    // The service worker appends ?pendingCall=<phone> to the URL in that case.
    const params = new URLSearchParams(window.location.search)
    const pendingCall = params.get('pendingCall')
    if (pendingCall) {
      window.history.replaceState({}, '', window.location.pathname)
      this.showCallHandoverModal(decodeURIComponent(pendingCall))
    }

    // Re-validate the push subscription every time the app regains focus. PWA push
    // subscriptions (particularly iOS Safari) can silently expire while the app is
    // backgrounded/closed for a few days, so re-checking only on mount isn't enough.
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  componentWillUnmount() {
    // Remove i18next handle
    i18n.off('initialized', this.onI18nInitialized)

    // Remove version ping interval
    if (this.versionPing) clearInterval(this.versionPing)

    // Remove service worker message listener
    if (navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this.onServiceWorkerMessage)
    }

    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }

  onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.pushNotificationSetup().catch(console.error)
    }
  }

  onActionCableConnected() {
    console.log('[WorkspaceChannel] connected')
  }

  onActionCableDisconnected() {
    console.log('[WorkspaceChannel] disconnected')
  }

  onActionCableReceived(event) {
    console.log('[WorkspaceChannel] event received', event)

    const {
      updateWorkspace,
      updateSubscription,
      startTimer,
      pauseTimer,
      stopTimer,
      currentUser,
    } = this.props

    switch (event.type) {
      case WorkspaceChannelEventType.WORKSPACE_CREATE:
      case WorkspaceChannelEventType.WORKSPACE_UPDATE:
        updateWorkspace(event.data.workspace)
        break
      case WorkspaceChannelEventType.SUBSCRIPTION_CREATE:
      case WorkspaceChannelEventType.SUBSCRIPTION_UPDATE:
        updateSubscription(event.data.subscription)
        break
      case WorkspaceChannelEventType.TIME_ENTRY_CREATE:
        // Only start timer if entry is owned by the user and currently running
        if (event.data.entry.user_id === currentUser.id && event.data.entry.active) {
          startTimer(event.data.entry)
        }
        break
      case WorkspaceChannelEventType.TIME_ENTRY_UPDATE:
        // Only sync timer if entry is owned by the user
        if (event.data.entry.user_id === currentUser.id) {
          if (event.data.entry.active) {
            startTimer(event.data.entry)
          } else if (event.data.timer_action === TimeEntryTimerAction.STOP) {
            stopTimer()
            document.title = 'Bizzey'
          } else if (event.data.timer_action === TimeEntryTimerAction.PAUSE) {
            pauseTimer(event.data.entry)
            document.title = 'Bizzey'
          }
        }
        break
      case WorkspaceChannelEventType.TIME_ENTRY_DELETE:
        // Only stop timer if the deleted entry is owned by the user
        if (event.data.user_id === currentUser.id) {
          stopTimer()
          document.title = 'Bizzey'
        }
        break
      case WorkspaceChannelEventType.SETTING_UPDATE:
        this.fetchSettings().catch(console.error)
        break
      case WorkspaceChannelEventType.CALL:
        if (event.data.user_id === currentUser.id && isMobile()) {
          this.showCallHandoverModal(event.data.telephone_number)
        }
        break
    }
  }

  async fetchSettings() {
    const { updateSettings } = this.props

    try {
      // Fetch remote settings
      const settings = await SettingsController.get()

      // Update local settings
      updateSettings(settings)
    } catch (ex) {
      console.error(ex)
    }
  }

  async pushNotificationSetup() {
    const permissionRequest = await PushNotification.requestPermission()

    if (permissionRequest === 'granted') {
      try {
        const token = await PushNotification.getToken()

        if (token) await DeviceController.syncDevice({ token: token })

        await PushNotification.onMessage(this.onPushNotificationMessage)
      } catch (ex) {
        console.error(ex)
      }
    }
  }

  showCallHandoverModal(phoneNumber: string) {
    const { t } = this.props

    this.props.showConfirmModal({
      title: t('App::Call initiation'),
      description: t('App::Are you sure you want to call {{telephoneNumber}}?', { telephoneNumber: phoneNumber }),
      action: { label: t('App::Start call') },
      onConfirm: () => {
        window.location.href = `tel:${phoneNumber}`
      },
    })
  }

  onServiceWorkerMessage(event: MessageEvent) {
    if (event.data?.type === 'CALL_HANDOVER' && event.data?.telephone_number) {
      this.showCallHandoverModal(event.data.telephone_number)
    }

    // The service worker rotated the push subscription on its own (e.g. the browser
    // invalidated the old one) — re-fetch and re-sync the new token right away.
    if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
      this.pushNotificationSetup().catch(console.error)
    }
  }

  async onPushNotificationMessage(payload: MessagePayload) {
    // @ts-ignore
    const pushNotificationData = (payload.data as PushNotificationData)

    switch (pushNotificationData?.type) {
      case PushNotificationDataType.CALL:
        this.showCallHandoverModal(pushNotificationData.telephone_number)
        break
      default:
        break
    }
  }

  onI18nInitialized() {
    const { currentUser } = this.props
    setLocale(currentUser.locale)
  }

  async onAppVersionPing() {
    const { appVersion, t } = this.props
    const response = await fetch('/version')

    if (response.status === 200) {
      const serverVersion = await response.text()

      if (appVersion !== serverVersion) {
        const confirmed = confirm(t('App::A new version of Bizzey is available. Would you like to reload the app to update?'))

        if (confirmed) {
          window.location.reload()
        }
      }
    }
  }

  shouldShowPaywall = (): boolean => {
    const { currentUser } = this.props

    return !currentUser.workspace.vip && (CurrentUserHelper.hasFreeSubscription() || !CurrentUserHelper.isSubscriptionActive())
  }

  render(): JSX.Element {
    const { currentUser, navigationActive } = this.props

    return (
      <TooltipProvider>
        <Helmet>
          <meta name="theme-color" content={navigationActive ? '#000000' : '#ffffff'} />
          <meta name="apple-mobile-web-app-status-bar-style" content={navigationActive ? 'black' : 'default'} />
        </Helmet>
        {navigationActive && <Body className='no-overflow' />}
        <ActionCableConsumer
          channel={{ channel: 'WorkspaceChannel', id: currentUser?.workspace?.id }}
          onConnected={this.onActionCableConnected}
          onDisconnected={this.onActionCableDisconnected}
          onReceived={this.onActionCableReceived}
        >
          <Router>
            <Switch>
              <Route path={ERoute.PATH_CONFIRM_EMAIL} component={ConfirmEmail} />
              <Route path={ERoute.PATH_WELCOME_LAYOUT} component={Onboarding} />
              <Route path={ERoute.PATH_WORKSPACES} component={Workspaces} />
              {UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) && <Route path={ERoute.PATH_INVOICES_NEW} render={(routeProps) => this.shouldShowPaywall() ? <Redirect to={ERoute.PATH_SUBSCRIPTION_PAYWALL} /> : <LedgerItemEditor {...routeProps} />} />}
              {UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) && <Route path={ERoute.PATH_INVOICE_EDIT} render={(routeProps) => this.shouldShowPaywall() ? <Redirect to={ERoute.PATH_SUBSCRIPTION_PAYWALL} /> : <LedgerItemEditor {...routeProps} />} />}
              <Route path={ERoute.PATH_INTEGRATION} render={(routeProps) => this.shouldShowPaywall() ? <Redirect to={ERoute.PATH_SUBSCRIPTION_PAYWALL} /> : <Integration {...routeProps} />} />
              <Route path={ERoute.PATH_PLAYBOOKS_CREATE} render={(routeProps) => this.shouldShowPaywall() ? <Redirect to={ERoute.PATH_SUBSCRIPTION_PAYWALL} /> : <PlaybooksCreate {...routeProps} />} />
              <Route path={ERoute.PATH_CONTRACTS_CREATE} render={(routeProps) => this.shouldShowPaywall() ? <Redirect to={ERoute.PATH_SUBSCRIPTION_PAYWALL} /> : <ContractsCreate {...routeProps} />} />
              <Route path={ERoute.PATH_FORMS_CREATE} render={(routeProps) => this.shouldShowPaywall() ? <Redirect to={ERoute.PATH_SUBSCRIPTION_PAYWALL} /> : <FormsCreate {...routeProps} />} />
              <Route path={ERoute.PATH_SEQUENCES_CREATE} render={(routeProps) => this.shouldShowPaywall() ? <Redirect to={ERoute.PATH_SUBSCRIPTION_PAYWALL} /> : <SequencesCreate {...routeProps} />} />
              <Route path={ERoute.PATH_SUBSCRIPTION_PAYWALL} component={() => <Subscription paywall />} />
              <Route path={ERoute.PATH_APP_LAYOUT} render={(navigation) => {
                // Email confirmation
                const shouldVerify = moment().diff(moment(currentUser.created_at), 'hours') >= 1

                // Email confirmation is required after 1 hour
                if (!currentUser.email_confirmed_at && shouldVerify) {
                  return <Redirect to={ERoute.PATH_CONFIRM_EMAIL} />
                }

                // User has no workspace assigned, redirect to workspaces screen
                if (!currentUser.workspace) {
                  return <Redirect to={ERoute.PATH_WORKSPACES} />
                }

                // Onboarding
                if (!currentUser.workspace.did_onboarding) {
                  return <Redirect to={ERoute.PATH_WELCOME_LAYOUT} />
                }

                // Lock the user out of the workspace if subscription has been suspended or unpaid
                if (this.shouldShowPaywall()) {
                  return <Redirect to={ERoute.PATH_SUBSCRIPTION_PAYWALL} />
                }

                // Redirect if onboarding not complete
                return <AppLayout {...navigation} />
              }} />
            </Switch>
            <Modals />
          </Router>
        </ActionCableConsumer>
        <Toaster position="bottom-center" />
      </TooltipProvider>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
      accessToken,
    },
    navigation: {
      active,
    },
    config: {
      version
    }
  } = state

  return {
    currentUser: currentUser,
    accessToken: accessToken,
    navigationActive: active,
    appVersion: version,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    updateWorkspace: (workspace) => dispatch(updateWorkspace(workspace)),
    updateSubscription: (subscription) => dispatch(updateSubscription(subscription)),
    startTimer: (entry) => dispatch(startTimer(entry)),
    pauseTimer: (entry) => dispatch(pauseTimer(entry)),
    stopTimer: () => dispatch(stopTimer()),
    updateSettings: (settings: Settings) => dispatch(updateSettings(settings)),
    showConfirmModal: (options) => dispatch(showConfirmModal(options)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(App))
